


import {
  defineComponent,
  onMounted,
  onBeforeUnmount,
  computed,
  reactive,
  ref,
} from '@vue/composition-api';

import type { StandardReport, StandardState } from '@/models/externalReports/standard/spStandardReport';
import type { StandardCar } from '@/models/externalReports/standard/standardCar';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import { UserActionTypes } from '@/store/modules/user';
import useGeolocation from '@/composables/externalReports/useGeolocation';
import standardCarApi from '@/apis/externalReports/standard/standard_car';
import standardMtxApi from '@/apis/externalReports/standard/standard_mtx';
import { enableNoSleep, disableNoSleep } from '@/lib/noSleepUtil';
import {
  initCommonState,
  notifyWorkElapsedTimeSpeech,
} from '@/components/Sp/ExternalReports/Standard/utils';
import {
  timeDifferenceInSeconds,
  secondsToTimeInteger,
  timeInteger,
} from '@/lib/dateTimeHelper';
import useSpeech from '@/composables/useSpeech';
import type { StandardMtx, StandardMtxsCreateParams } from '@/models/apis/externalReports/standard/standardMtxRequest';
import { useRequestGuard } from '@/composables/useRequestGuard';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import DepartureScreen from '@/components/Sp/ExternalReports/Standard/DepartureScreen/index.vue';
import PreWorkScreen from '@/components/Sp/ExternalReports/Standard/PreWorkScreen/index.vue';
import WorkingScreen from '@/components/Sp/ExternalReports/Standard/WorkingScreen/index.vue';
import PostWorkScreen from '@/components/Sp/ExternalReports/Standard/PostWorkScreen/index.vue';
import SaveWorkSituationScreen from '@/components/Sp/ExternalReports/Standard/SaveWorkSituation/index.vue';
import PhotoMemoScreen from '@/components/Sp/ExternalReports/Standard/PhotoMemoScreen/index.vue';
import { dtFormat } from '@/lib/dateHelper';
import { EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/standard';
import type { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import { EXTERNAL_REPORT_ABILITY_DRIVER } from '@/consts/externalReports/external_reports';
import { LocalStorageActionTypes, LocalStorageGetterTypes } from '@/store/modules/localStorage';

const STANDARD_CAR_UPDATE_TIMER_SEC = 10;
const WORK_ELAPSED_TIME_NOTIFY_TIMER_SEC = 1;
const STANDARD_MTX_STORE_TIMER_SEC = 10; // 1秒だとさすがに多すぎた
const STANDARD_MTX_CREATE_TIMER_SEC = 60;

export default defineComponent({
  components: {
    DepartureScreen,
    PreWorkScreen,
    WorkingScreen,
    PostWorkScreen,
    SaveWorkSituationScreen,
    PhotoMemoScreen,
  },
  setup() {
    const state = reactive<StandardState>(initCommonState());
    const store = useStore();
    const { router } = useRoute();
    const userState = store.state.user;
    const { doSpeech } = useSpeech();
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      showErrorMsg(errorMessage);
    });

    const workElapsedTimeInt = computed<number>(() => {
      if (!state.workStartTime || !state.currentTime) { return 0; }

      const workElapsedTimeSec = timeDifferenceInSeconds(state.workStartTime, state.workEndTime ?? state.currentTime);
      return secondsToTimeInteger(workElapsedTimeSec);
    });
    const standardUserSettings = ref<ExternalReportSettings | null>(null);
    const externalReportAbility = computed<ExternalReportAbility | null>(() => {
      return userState?.external_report_abilities?.find(
        ability => ability.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;
    });
    const statusWorking = computed<string>(() => {
      return standardUserSettings.value?.choices.status.working.key ?? 'working';
    });
    const statusRunning = computed<string>(() => {
      return standardUserSettings.value?.choices.status.running.key ?? 'moving';
    });
    const statusStopped = computed<string>(() => {
      return standardUserSettings.value?.choices.status.stopped.key ?? 'stopped';
    });

    const currentComponent = computed<string>(() => {
      if (state.currentScreenOverride) {
        return state.currentScreenOverride;
      }

      const { currentScreen } = getCurrentScreenAndStatus();
      return currentScreen;
    });
    const isImagesEditing = computed<boolean>(() => {
      return state.currentScreenOverride === 'PhotoMemoScreen';
    });
    const localStorageKey = LocalStorageGetterTypes.STANDARD_REPORT_SP_STORAGE;

    const stopAll = async() => {
      clearTimers();
      await doRequest(async() => {
        updateCarStatus(statusStopped.value);
      }, '車両の状態更新に失敗しました。再度操作を行ってください。');
    };
    const geolocationState = useGeolocation(stopAll);
    const currentLocation = computed(() => geolocationState.currentLocation);

    const saveLocalStorage = (): void => {
      store.dispatch(LocalStorageActionTypes.SET, { key: localStorageKey, val: { standardCar: state.standardCar } });
    };
    const clearLocalStorage = (): void => {
      store.dispatch(LocalStorageActionTypes.SET, { key: localStorageKey, val: null });
    };
    const loadStandardCarDataFromLocalStorage = (): void => {
      const standardReportLocalStorage = store.getters[localStorageKey];
      if (!standardReportLocalStorage) return;

      state.standardCar = standardReportLocalStorage.standardCar;
      state.baseDepartureTime = state.standardCar.report.baseDepartureTs ?? null;
      state.workStartTime = state.standardCar.report.operationStartTs ?? null;
      state.workEndTime = state.standardCar.report.operationEndTs ?? null;
      const { status } = getCurrentScreenAndStatus();
      state.status = status;
    };
    const getCurrentScreenAndStatus = (): { currentScreen: string; status: string } => {
      const report = state.standardCar.report;
      if (!report.baseDepartureTs) return { currentScreen: 'DepartureScreen', status: statusStopped.value };
      if (!report.operationStartTs) return { currentScreen: 'PreWorkScreen', status: statusRunning.value };
      if (!report.operationEndTs) return { currentScreen: 'WorkingScreen', status: statusWorking.value };
      if (!report.baseArrivalTs) return { currentScreen: 'PostWorkScreen', status: statusRunning.value };

      return { currentScreen: 'DepartureScreen', status: statusStopped.value };
    };
    const updateCommonState = (status: string | null, report: StandardReport): void => {
      state.status = status ?? state.status;
      state.standardCar.report = report;
      state.baseDepartureTime = report.baseDepartureTs ? report.baseDepartureTs : null;
      state.currentScreenOverride = null;
      saveLocalStorage();
    };
    const updateCommonStateAndCarStatus = async(status: string, report: StandardReport): Promise<void> => {
      updateCommonState(status, report);
      await doRequest(async() => {
        await updateCarStatus(status);
      }, '車両の状態更新に失敗しました。再度操作を行ってください。');
    };
    const updateCarStatus = async(status: string): Promise<StandardCar | null> => {
      if (!geolocationState.currentLocation) { return null; }

      const { lat, lon } = geolocationState.currentLocation;
      state.status = status ?? state.status;
      const reqObj = {
        lat: lat,
        lon: lon,
        status: state.status,
      };
      const standardCar = await standardCarApi.createOrUpdate(reqObj);
      state.standardCar.id = standardCar.id;
      saveLocalStorage();

      return standardCar;
    };
    const clearTimers = () => {
      if (state.carUpdateTimer) {
        clearTimeout(state.carUpdateTimer);
      }
      if (state.workElapsedTimeNotifyTimer) {
        clearInterval(state.workElapsedTimeNotifyTimer);
      }
      if (state.mtxStoreTimer) {
        clearInterval(state.mtxStoreTimer);
      }
      if (state.mtxCreateTimer) {
        clearTimeout(state.mtxCreateTimer);
      }
      state.carUpdateTimer = null;
      state.workElapsedTimeNotifyTimer = null;
      state.mtxStoreTimer = null;
      state.mtxCreateTimer = null;
    };
    const storeStandardMtx = () => {
      if (!geolocationState.currentLocation) { return; }
      if (state.standardCar.id === 0 || state.standardCar.report.id === 0) { return; }

      const now = new Date();
      if (state.lastStandardMtxTs) {
        const thres = 1000; // milliseconds
        const diffMSec = now.getTime() - state.lastStandardMtxTs.getTime();
        if (diffMSec < thres) { return; }
      }

      const { lat, lon } = geolocationState.currentLocation;
      const obj: StandardMtx = {
        ts: now,
        lat: lat,
        lon: lon,
        status: state.status,
      };
      state.standardMtxs.push(obj);
      state.lastStandardMtxTs = now;
    };
    const startMoving = async(status: string, report: StandardReport): Promise<void> => {
      await updateCommonStateAndCarStatus(status, report);
      await restartAllIntervals();
    };
    const startWorking = async(status: string, report: StandardReport): Promise<void> => {
      await updateCommonStateAndCarStatus(status, report);
      state.workStartTime = new Date();
    };
    const stopRunning = async(status: string, report: StandardReport): Promise<void> => {
      updateCommonState(status, report);
      stopAll();
    };
    const saveStandardMtxs = async() => {
      if (state.standardMtxs.length < 2) { return; }

      const standardMtxs = state.standardMtxs.splice(0);
      const reqObj: StandardMtxsCreateParams = {
        car_id: state.standardCar.id,
        report_id: state.standardCar.report.id,
        mtxs: standardMtxs,
      };
      await standardMtxApi.createStandardMTXs(reqObj);
    };
    const editContentAndRoad = () => {
      state.currentScreenOverride = 'SaveWorkSituationScreen';
    };
    const addPhotoMemo = () => {
      state.currentScreenOverride = 'PhotoMemoScreen';
    };
    const notifyWorkElapsedTime = () => {
      // 現在時刻の更新
      state.currentTime = new Date();
      notifyWorkElapsedTimeSpeech(workElapsedTimeInt.value, doSpeech);
    };
    const doStopWorking = async() => {
      await doRequest(async() => {
        await Promise.all([
          saveStandardMtxs(),
          updateCarStatus(statusRunning.value),
        ]);
      }, '作業終了に失敗しました。再度操作を行ってください。');
    };
    const suspendWork = async(status: string, report: StandardReport) => {
      if (state.workElapsedTimeNotifyTimer) {
        clearInterval(state.workElapsedTimeNotifyTimer);
      }
      state.workElapsedTimeNotifyTimer = null;
      await doStopWorking();
      updateCommonState(status, report);
      state.workEndTime = new Date();
    };
    const startCarStatusLoop = () => {
      const loop = async() => {
        try {
          await updateCarStatus(state.status);
        } catch (error) {
          console.error('Failed to update car status in loop:', error);
        }

        state.carUpdateTimer = window.setTimeout(loop, STANDARD_CAR_UPDATE_TIMER_SEC * 1000);
      };
      loop();
    };
    const restartStandardCarUpdateInterval = async() => {
      if (state.carUpdateTimer) {
        clearTimeout(state.carUpdateTimer);
      }
      startCarStatusLoop();
    };
    const restartWorkElapsedTimeNotifyInterval = () => {
      if (state.workElapsedTimeNotifyTimer) {
        clearInterval(state.workElapsedTimeNotifyTimer);
      }
      state.workElapsedTimeNotifyTimer = window.setInterval(notifyWorkElapsedTime, WORK_ELAPSED_TIME_NOTIFY_TIMER_SEC * 1000);
    };
    const startMtxCreateLoop = () => {
      const loop = async() => {
        try {
          await saveStandardMtxs();
        } catch (error) {
          console.error('Failed to create MTX in loop:', error);
        }

        state.mtxCreateTimer = window.setTimeout(loop, STANDARD_MTX_CREATE_TIMER_SEC * 1000);
      };
      loop();
    };
    const restartStandardMtxUpdateInterval = () => {
      state.standardMtxs = [];
      if (state.mtxStoreTimer) {
        clearInterval(state.mtxStoreTimer);
      }
      if (state.mtxCreateTimer) {
        clearTimeout(state.mtxCreateTimer);
      }
      state.mtxStoreTimer = window.setInterval(storeStandardMtx, STANDARD_MTX_STORE_TIMER_SEC * 1000);
      startMtxCreateLoop();
    };
    const restartAllIntervals = async() => {
      clearTimers();
      await restartStandardCarUpdateInterval();
      restartWorkElapsedTimeNotifyInterval();
      restartStandardMtxUpdateInterval();
    };
    const showErrorMsg = (errMsg: string) => {
      state.errorModalMsg = errMsg;
      state.showErrorModal = true;
    };
    const logout = async() => {
      await stopAll();
      await doRequest(async() => {
        await store.dispatch(UserActionTypes.LOGOUT);
      }, 'ログアウトに失敗しました。再度操作を行ってください。');
      router.push({ name: 'Login' });
    };

    onMounted(async() => {
      enableNoSleep();
      loadStandardCarDataFromLocalStorage();
      await doRequest(async() => {
        await waitForUserAndMasters();
        standardUserSettings.value = window.master.externalReportSettings?.find(
          setting => setting.external_report_type === EXTERNAL_REPORT_TYPE,
        ) ?? null;
        if (!externalReportAbility.value || !standardUserSettings.value) {
          router.replace({ name: 'Login' });
          return;
        }
        const abilities = standardUserSettings.value?.choices.abilities;
        const ability = abilities?.find(ability => ability.key === externalReportAbility.value?.ability_id);
        if (!ability) {
          router.replace({ name: 'Login' });
          return;
        }

        if (!ability.name.endsWith(EXTERNAL_REPORT_ABILITY_DRIVER)) {
          router.replace({ name: 'Login' });
          return;
        }
        if (state.standardCar.report.id !== 0 && state.status !== statusStopped.value) {
          await restartAllIntervals();
        }
        if (!geolocationState.currentLocation) {
          let count = 100;
          while (count-- > 0 && !geolocationState.currentLocation) {
            await new Promise(resolve => window.setTimeout(resolve, 100));
          }
        }
        await updateCarStatus(state.status);
      }, 'ユーザー情報の読み込みに失敗しました。再度操作を行ってください。');
      if (!standardUserSettings.value) {
        router.push({ name: 'Login' });
        return;
      }

      state.externalReportSpLabels = standardUserSettings.value.sp;
    });

    onBeforeUnmount(() => {
      clearTimers();
      clearLocalStorage();
      disableNoSleep();
    });

    return {
      state,
      geolocationState,
      currentComponent,
      currentLocation,
      isRequesting,
      isImagesEditing,
      userState,
      workElapsedTimeInt,
      standardUserSettings,
      externalReportAbility,
      statusStopped,
      addPhotoMemo,
      doStopWorking,
      dtFormat,
      editContentAndRoad,
      logout,
      showErrorMsg,
      suspendWork,
      startMoving,
      startWorking,
      stopRunning,
      timeInteger,
      updateCommonState,
      updateCommonStateAndCarStatus,
    };
  },
});
